import { useState, useEffect, useRef, lazy, Suspense } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Plus, Compass, ChevronDown } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "@/hooks/useLanguage";
import { useScrollProgress } from "@/hooks/useScrollParallax";
import HeroParticles from "@/components/HeroParticles";

const InteractiveGlobe = lazy(() => import("@/components/InteractiveGlobe"));

interface HeroProps {
  onCreateClick: () => void;
}

const ROTATING_COUNTRIES = [
  { fr: "au Canada", en: "to Canada", es: "a Canadá", flag: "🇨🇦" },
  { fr: "aux États-Unis", en: "to the USA", es: "a Estados Unidos", flag: "🇺🇸" },
  { fr: "en Norvège", en: "to Norway", es: "a Noruega", flag: "🇳🇴" },
  { fr: "en Chine", en: "to China", es: "a China", flag: "🇨🇳" },
  { fr: "en Australie", en: "to Australia", es: "a Australia", flag: "🇦🇺" },
  { fr: "en Namibie", en: "to Namibia", es: "a Namibia", flag: "🇳🇦" },
  { fr: "au Maroc", en: "to Morocco", es: "a Marruecos", flag: "🇲🇦" },
  { fr: "en Inde", en: "to India", es: "a India", flag: "🇮🇳" },
  { fr: "au Japon", en: "to Japan", es: "a Japón", flag: "🇯🇵" },
  { fr: "en Espagne", en: "to Spain", es: "a España", flag: "🇪🇸" },
  { fr: "en Indonésie", en: "to Indonesia", es: "a Indonesia", flag: "🇮🇩" },
];

const Hero = ({ onCreateClick }: HeroProps) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { getLocalizedPath } = useLanguage();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isExiting, setIsExiting] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);
  const [scrollRef, scrollProgress] = useScrollProgress();

  // Delay scroll transform so the hero starts at full size/opacity
  const revealProgress = Math.max(0, (scrollProgress - 0.14) / 0.86);
  const heroScale = 1 - revealProgress * 0.08;

  const lang = i18n.language?.substring(0, 2) || "fr";

  useEffect(() => {
    const interval = setInterval(() => {
      setIsExiting(true);
      setTimeout(() => {
        setCurrentIndex((prev) => (prev + 1) % ROTATING_COUNTRIES.length);
        setIsExiting(false);
      }, 400);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  const currentCountry = ROTATING_COUNTRIES[currentIndex];
  const countryLabel = currentCountry[lang as "fr" | "en" | "es"] || currentCountry.fr;

  const scrollToContent = () => {
    const nextSection = sectionRef.current?.nextElementSibling;
    nextSection?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      ref={(el) => {
        (sectionRef as React.MutableRefObject<HTMLElement | null>).current = el;
        (scrollRef as React.MutableRefObject<HTMLDivElement | null>).current = el as HTMLDivElement | null;
      }}
      className="relative w-full h-[100svh] min-h-[600px] flex items-center overflow-hidden"
    >
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-[hsl(215,25%,8%)] via-[hsl(210,30%,6%)] to-[hsl(220,35%,4%)]" />


      {/* Content grid: text left, globe right */}
      <div className="relative z-10 w-full px-6 sm:px-10 lg:px-16 grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
        {/* Left: text content */}
        <div className="text-left space-y-6 animate-fade-in">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight">
            {t('hero.titlePrefix')}{" "}
            <span className="inline-block relative">
              <span
                key={currentIndex}
                className={`inline-block text-[hsl(var(--secondary))] transition-all duration-400 ${
                  isExiting 
                    ? "opacity-0 translate-y-3" 
                    : "opacity-100 translate-y-0"
                }`}
              >
                {countryLabel}
              </span>
            </span>
          </h1>
          <p className="text-lg sm:text-xl text-white/75 max-w-lg leading-relaxed">
            {t('hero.description')}
          </p>
          
          <div className="flex flex-col sm:flex-row gap-3 pt-2">
            <Button 
              size="lg" 
              className="group bg-primary hover:bg-primary/90 shadow-lg shadow-primary/25 hover:shadow-xl hover:shadow-primary/30 transition-all duration-300 hover:scale-105 active:scale-95 text-base" 
              onClick={onCreateClick}
            >
              <Plus className="mr-2 h-5 w-5 transition-transform duration-300 group-hover:rotate-90" />
              {t('nav.createTrip')}
            </Button>
            <Button 
              size="lg" 
              variant="outline" 
              className="group bg-white/10 hover:bg-white/20 text-white border-white/20 hover:border-white/40 shadow-lg transition-all duration-300 hover:scale-105 active:scale-95 text-base" 
              onClick={() => navigate(getLocalizedPath("/explore"))}
            >
              <Compass className="mr-2 h-5 w-5 transition-transform duration-300 group-hover:rotate-[360deg]" />
              {t('nav.explore')}
            </Button>
          </div>
        </div>

        {/* Right: 3D Globe — visible from md breakpoint */}
        <div className="hidden md:flex items-center justify-center self-stretch min-h-[380px] md:min-h-[420px] lg:min-h-[520px] xl:min-h-[620px] 2xl:min-h-[700px] overflow-visible">
          <Suspense fallback={
            <div className="w-full h-full flex items-center justify-center">
              <div className="w-24 h-24 rounded-full border-2 border-primary/20 shadow-[0_0_40px_hsl(200_85%_38%/0.15)] animate-pulse" />
            </div>
          }>
            <InteractiveGlobe className="w-full h-full" activeCountryIndex={currentIndex} />
          </Suspense>
        </div>

        {/* Mobile: smaller 3D globe */}
        <div className="md:hidden flex justify-center" style={{ height: "280px" }}>
          <Suspense fallback={
            <div className="w-full h-full flex items-center justify-center">
              <div className="w-20 h-20 rounded-full border-2 border-primary/20 animate-pulse" />
            </div>
          }>
            <InteractiveGlobe className="w-full h-full" activeCountryIndex={currentIndex} />
          </Suspense>
        </div>
      </div>

      {/* Scroll indicator */}
      <button 
        onClick={scrollToContent}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white/50 hover:text-white/80 transition-colors animate-bounce z-10"
        aria-label="Scroll down"
      >
        <ChevronDown className="h-8 w-8" />
      </button>
    </section>
  );
};

export default Hero;
